import { useCallback, useEffect, useState } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import { drawingApi, DrawingVersionModel } from "../api/drawingApi";
import { BoxArrowInDownRight, Trash } from "react-bootstrap-icons";

interface VersionManagementModalProps {
  vehicleFileNumber: string;
  onClose: (modified: boolean) => void;
}

function VersionManagementModal({
  vehicleFileNumber,
  onClose,
}: VersionManagementModalProps) {
  const [error, setError] = useState<Problem | string | undefined>();
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<DrawingVersionModel[]>([]);
  const [confirmingText, setConfirmingText] = useState("");
  const [confirmingAction, setConfirmingAction] = useState<() => void>();

  const reload = useCallback(async () => {
    setLoading(true);

    const result = await drawingApi.getVersions(vehicleFileNumber);

    if (result.ok) {
      setVersions(result.value);
      setError(undefined);
    } else {
      setError(result.problem);
    }

    setLoading(false);
  }, [vehicleFileNumber]);

  useEffect(() => {
    reload();
  }, [reload, vehicleFileNumber]);

  const loadVersion = async (version: number) => {
    setLoading(true);
    const result = await drawingApi.importVersion(vehicleFileNumber, version);

    setConfirmingAction(undefined);
    setLoading(false);

    if (result.ok) {
      handleClose(true);
    } else {
      setError(result.problem);
    }
  };

  const removeVersion = async (version: number) => {
    setLoading(true);
    const result = await drawingApi.removeVersion(vehicleFileNumber, version);

    setConfirmingAction(undefined);
    setLoading(false);

    if (result.ok) {
      setError(undefined);
      await reload();
    } else {
      setError(result.problem);
    }
  };

  const handleClose = (modified: boolean) => {
    setVersions([]);
    setLoading(false);
    setConfirmingAction(undefined);
    onClose(modified);
  };

  const handleLoadVersion = (version: number) => {
    setConfirmingText(
      "The version will selected will be loaded and replace the current drawing.",
    );
    setConfirmingAction(() => () => loadVersion(version));
  };

  const handleRemoveVersion = async (version: number) => {
    setConfirmingText("This version will be removed!");
    setConfirmingAction(() => () => removeVersion(version));
  };

  return (
    <Modal
      show={true}
      size="lg"
      onHide={() => {
        handleClose(false);
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Version management</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {confirmingAction && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              padding: "var(--bs-modal-padding)",
              backgroundColor: "white",
              zIndex: 100,
            }}
          >
            <h3>Are you sure?</h3>
            <p>{confirmingText}</p>
          </div>
        )}
        <ErrorDisplay error={error} />
        <div className="fixed-table-header" style={{ maxHeight: 400 }}>
          <Table size="sm" variant="hover">
            <thead>
              <tr>
                <th>Version</th>
                <th className="text-nowrap">Created by</th>
                <th className="text-nowrap">Created date</th>
                <th>Release</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {versions.length > 0 ? (
                versions.map((v) => (
                  <tr key={v.version.toString()}>
                    <td>{v.version.toString()}</td>
                    <td className="text-nowrap">{v.createdBy}</td>
                    <td
                      className="text-nowrap"
                      title={
                        v.createdOn.toDateString() +
                        " " +
                        v.createdOn.toTimeString()
                      }
                    >
                      {v.createdOn.toDateString()}
                    </td>
                    <td className="text-nowrap">
                      {v.releaseName ?? "<unreleased>"}
                    </td>
                    <td className="text-nowrap" style={{ width: 1 }}>
                      <Button
                        size="sm"
                        className="mx-1"
                        title="Replace the drawing with this version"
                        onClick={() => handleLoadVersion(v.version)}
                        disabled={loading}
                      >
                        <BoxArrowInDownRight className="bi" />
                      </Button>
                      <Button
                        size="sm"
                        className="mx-1"
                        title="Remove this version"
                        onClick={() => handleRemoveVersion(v.version)}
                        disabled={loading}
                      >
                        <Trash className="bi" />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="mb-2">
                  <td colSpan={5}>
                    {loading ? "loading..." : "No versions where found."}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {confirmingAction && (
          <>
            <Button
              variant="danger"
              size="sm"
              onClick={confirmingAction}
              style={{ marginRight: "auto" }}
              disabled={loading}
            >
              Confirm
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirmingAction(undefined)}
              disabled={loading}
            >
              Cancel
            </Button>
          </>
        )}
        {!confirmingAction && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleClose(false)}
            >
              Close
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default VersionManagementModal;
